import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Skeleton, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LogoutIcon from "@mui/icons-material/Logout";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import UpdateIcon from "@mui/icons-material/Update";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { getOeceStats, Stats } from "../services/oece.service";
import { C, Tarjeta, fechaHora, haceCuanto, num } from "./oeceUi";
import { BuscadorTab, CambiosTab, OportunidadesTab, SancionadosTab } from "./OeceTabs";
import OeceFichaDrawer from "./OeceFichaDrawer";
import CautelaresTab from "./CautelaresTab";
import CandidatosTab from "./CandidatosTab";

const TABS = [
  { key: "oportunidades", label: "Oportunidades", icon: <BoltIcon /> },
  { key: "candidatos", label: "Candidatos a cautelar", icon: <PersonSearchIcon /> },
  { key: "buscador", label: "Buscador", icon: <SearchIcon /> },
  { key: "sancionados", label: "Sancionados vigentes", icon: <GavelIcon /> },
  { key: "cambios", label: "Cambios recientes", icon: <UpdateIcon /> },
  { key: "cautelares", label: "Cautelares", icon: <ShieldOutlinedIcon /> },
];

const Kpi = ({
  icono, valor, label, detalle, color, bg,
}: { icono: React.ReactNode; valor?: number; label: string; detalle?: string; color: string; bg: string }) => (
  <Tarjeta sx={{ p: 2.25, display: "flex", gap: 1.75, alignItems: "flex-start" }}>
    <Box sx={{ width: 42, height: 42, borderRadius: 2.5, bgcolor: bg, color, display: "grid", placeItems: "center", flexShrink: 0 }}>
      {icono}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      {valor === undefined ? (
        <Skeleton width={90} height={34} />
      ) : (
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: C.navy, lineHeight: 1.2 }}>{num(valor)}</Typography>
      )}
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{label}</Typography>
      {detalle && <Typography sx={{ fontSize: 12, color: C.muted }}>{detalle}</Typography>}
    </Box>
  </Tarjeta>
);

const OecePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [tab, setTab] = useState(() => {
    const guardada = localStorage.getItem("oece_tab");
    return TABS.some((t) => t.key === guardada) ? guardada! : "oportunidades";
  });
  const [ruc, setRuc] = useState<string | null>(null);
  const esRolOece = localStorage.getItem("rol") === "OECE";

  useEffect(() => {
    getOeceStats().then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cambiarTab = (t: string) => {
    setTab(t);
    localStorage.setItem("oece_tab", t);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const corrida = stats?.ultima_corrida;
  const enCurso = corrida && !corrida.fin;
  const maxTipo = Math.max(1, ...(stats?.rnp_vigentes_por_tipo.map((t) => t.proveedores) ?? [1]));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: C.bg }}>
      {/* ── HERO ── */}
      <Box
        sx={{
          background: `radial-gradient(1200px 400px at 85% -10%, rgba(200,162,74,.22), transparent 60%),
                       linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`,
          color: "#fff", px: { xs: 2, md: 5 }, pt: 2.5, pb: 11,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
          {/* El rol OECE solo tiene esta vista: no hay panel al que volver */}
          {esRolOece ? <span /> : <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/")}
            sx={{ color: "rgba(255,255,255,.85)", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,.08)" } }}
          >
            Volver al panel
          </Button>}
          <Button
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ color: "rgba(255,255,255,.85)", textTransform: "none", "&:hover": { bgcolor: "rgba(255,255,255,.08)" } }}
          >
            Salir
          </Button>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-end" }} spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 12, letterSpacing: 2, color: C.gold, fontWeight: 700 }}>
              OECE · CONTRATACIONES PÚBLICAS
            </Typography>
            <Typography sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 800, lineHeight: 1.15, mt: 0.5 }}>
              Proveedores del Estado
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,.72)", mt: 1, maxWidth: 620 }}>
              RNP, habilitación, sanciones del Tribunal, inhabilitaciones y penalidades de todos los proveedores,
              actualizado cada día desde la fuente oficial.
            </Typography>
          </Box>

          <Tooltip title={corrida ? `Corrida #${corrida.id} iniciada ${fechaHora(corrida.inicio)}` : ""}>
            <Box
              sx={{
                border: "1px solid rgba(255,255,255,.18)", bgcolor: "rgba(255,255,255,.06)",
                borderRadius: 3, px: 2, py: 1.25, backdropFilter: "blur(6px)", minWidth: 230,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 8, height: 8, borderRadius: "50%", bgcolor: enCurso ? C.gold : "#4ade80",
                    boxShadow: `0 0 0 4px ${enCurso ? "rgba(200,162,74,.25)" : "rgba(74,222,128,.2)"}`,
                    animation: enCurso ? "oecePulso 1.6s ease-in-out infinite" : "none",
                    "@keyframes oecePulso": { "50%": { opacity: 0.35 } },
                  }}
                />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {!stats ? "Cargando…" : enCurso ? "Actualizando desde OECE" : "Datos al día"}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.65)", mt: 0.25 }}>
                Última consulta {haceCuanto(stats?.ultima_consulta)} · {num(stats?.rucs_consultados)} RUCs revisados
              </Typography>
            </Box>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, mt: -8, pb: 6 }}>
        {/* ── KPIs ── */}
        <Box
          sx={{
            display: "grid", gap: 2, mb: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" },
          }}
        >
          <Kpi icono={<BusinessIcon />} valor={stats?.proveedores} label="Proveedores" detalle="Con ficha en OECE" color={C.navy2} bg="#e9eef7" />
          <Kpi icono={<VerifiedOutlinedIcon />} valor={stats?.rnp_vigentes} label="RNP vigente" detalle={stats ? `${Math.round((stats.rnp_vigentes / Math.max(1, stats.proveedores)) * 100)}% del total` : undefined} color={C.blue} bg={C.blueSoft} />
          <Kpi icono={<GavelIcon />} valor={stats?.sancionados_vigentes} label="Sancionados hoy" detalle="Sanción o inhabilitación vigente" color={C.red} bg={C.redSoft} />
          <Kpi icono={<ReportGmailerrorredIcon />} valor={stats?.con_sanciones} label="Con antecedentes" detalle="Alguna sanción en su historia" color="#8a6a1c" bg={C.goldSoft} />
          <Kpi icono={<ReceiptLongIcon />} valor={stats?.con_penalidades} label="Con penalidades" detalle="Aplicadas por entidades" color={C.amber} bg={C.amberSoft} />
          <Kpi icono={<DoNotDisturbOnOutlinedIcon />} valor={stats?.no_habilitados} label="No habilitados" detalle="No pueden contratar" color={C.muted} bg="#eef1f5" />
        </Box>

        {/* ── RNP por tipo ── */}
        <Tarjeta sx={{ p: 2.25, mb: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 1.5, md: 4 }} alignItems={{ md: "center" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.navy, whiteSpace: "nowrap" }}>
              RNP vigente por tipo
            </Typography>
            <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2.5 }}>
              {(stats?.rnp_vigentes_por_tipo ?? []).map((t) => (
                <Box key={t.codigo}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12.5, color: "#334155" }}>{t.nombre}</Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy }}>{num(t.proveedores)}</Typography>
                  </Stack>
                  <Box sx={{ height: 6, bgcolor: "#edf0f5", borderRadius: 3, overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${(t.proveedores / maxTipo) * 100}%`, bgcolor: C.navy2, borderRadius: 3 }} />
                  </Box>
                </Box>
              ))}
              {!stats && [0, 1, 2, 3].map((i) => <Skeleton key={i} height={30} />)}
            </Box>
          </Stack>
        </Tarjeta>

        {/* ── Pestañas ── */}
        <Tarjeta sx={{ overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => cambiarTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: 2, borderBottom: `1px solid ${C.border}`,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, minHeight: 56, color: C.muted },
              "& .Mui-selected": { color: `${C.navy} !important` },
              "& .MuiTabs-indicator": { bgcolor: C.gold, height: 3, borderRadius: "3px 3px 0 0" },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} icon={t.icon} iconPosition="start" />
            ))}
          </Tabs>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {tab === "oportunidades" && <OportunidadesTab onVerFicha={setRuc} />}
            {tab === "candidatos" && <CandidatosTab onVerFicha={setRuc} />}
            {tab === "buscador" && <BuscadorTab onVerFicha={setRuc} />}
            {tab === "sancionados" && <SancionadosTab onVerFicha={setRuc} />}
            {tab === "cambios" && <CambiosTab onVerFicha={setRuc} />}
            {tab === "cautelares" && <CautelaresTab onVerFicha={setRuc} />}
          </Box>
        </Tarjeta>
      </Box>

      <OeceFichaDrawer ruc={ruc} onClose={() => setRuc(null)} />
    </Box>
  );
};

export default OecePage;
